import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '@/lib/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { AuthContext } from '@/contexts/AuthContext';

export function useTokenAuth() {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);

  useEffect(() => {
    const authenticateFromUrl = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          // No token in URL, proceed normally
          setIsAuthenticating(false);
          return;
        }

        // SECURITY FIX: Clear token from URL immediately to prevent exposure in logs/history
        window.history.replaceState({}, '', location.pathname);

        // SECURITY: Send token in request body only, not in Authorization header
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify-mobile-token`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Token verification failed');
        }

        const { customToken, user } = await response.json();

        // Sign in to Firebase with custom token
        await signInWithCustomToken(auth, customToken);

        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(user));

        // Update auth context with user data
        if (authContext) {
          authContext.setUser(user);
        }

        // Navigate to home page
        navigate('/');
      } catch (err) {
        console.error('Token authentication failed:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed. Please log in.');
      } finally {
        setIsAuthenticating(false);
      }
    };

    authenticateFromUrl();
  }, [location.search, location.pathname, navigate, authContext]);

  return { isAuthenticating, error };
}
